import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ChefHat, Clock, Check, Play, UtensilsCrossed, 
  RefreshCw, Bell, Timer, Store, Volume2, VolumeX
} from 'lucide-react';
import { toast } from 'sonner';

// Order status flow: pending -> preparing -> ready -> served
const ORDER_STATUSES = {
  pending: { label: 'New', color: 'bg-red-500', icon: Bell, next: 'preparing' },
  preparing: { label: 'Preparing', color: 'bg-amber-500', icon: ChefHat, next: 'ready' },
  ready: { label: 'Ready', color: 'bg-green-500', icon: Check, next: 'served' },
  served: { label: 'Served', color: 'bg-blue-500', icon: UtensilsCrossed, next: null }
};

export default function KitchenDisplay() {
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState('all');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastOrderCount, setLastOrderCount] = useState(0);

  // Load restaurants
  useEffect(() => {
    const loadRestaurants = async () => {
      try {
        const res = await api.get('/restaurants');
        setRestaurants(res.data.filter(r => r.is_active !== false));
      } catch (error) {
        console.error('Failed to load restaurants');
      }
    };
    loadRestaurants();
  }, []);

  // Load orders
  const loadOrders = useCallback(async () => {
    try {
      let url = '/orders?';
      // Get orders that are not paid/cancelled and not served
      const statuses = ['pending', 'preparing', 'ready'];
      
      if (selectedRestaurant !== 'all') {
        url += `restaurant_id=${selectedRestaurant}&`;
      }
      
      const res = await api.get(url);
      
      // Filter for kitchen-relevant orders
      const kitchenOrders = res.data.filter(order => 
        statuses.includes(order.status) && 
        (selectedRestaurant === 'all' || order.restaurant_id === selectedRestaurant)
      );
      
      // Sort: pending first, then preparing, then ready
      const statusOrder = { pending: 0, preparing: 1, ready: 2 };
      kitchenOrders.sort((a, b) => {
        const statusDiff = statusOrder[a.status] - statusOrder[b.status];
        if (statusDiff !== 0) return statusDiff;
        return new Date(a.created_at) - new Date(b.created_at);
      });
      
      // Play sound for new orders
      if (soundEnabled && kitchenOrders.filter(o => o.status === 'pending').length > lastOrderCount) {
        playNotificationSound();
      }
      setLastOrderCount(kitchenOrders.filter(o => o.status === 'pending').length);
      
      setOrders(kitchenOrders);
    } catch (error) {
      console.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [selectedRestaurant, soundEnabled, lastOrderCount]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    loadOrders();
    
    if (autoRefresh) {
      const interval = setInterval(loadOrders, 10000);
      return () => clearInterval(interval);
    }
  }, [loadOrders, autoRefresh]);

  // Play notification sound
  const playNotificationSound = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH6LkZWTj4aBc2ptdH+JkZWTkIyFfHBpbXR/iJGVk5CMhXxwaW10f4iRlZOQjIV8cGltdH+IkZWTkIyFfHBpbXR/iJGVk5CMhXxwaW10f4g=');
      audio.volume = 0.5;
      audio.play().catch(() => {
        // Audio play failed - likely no user interaction yet
      });
    } catch (e) {
      // Audio not supported
    }
  };

  // Update order status
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await api.patch(`/orders/${orderId}/status?status=${newStatus}`);
      toast.success(`Order marked as ${ORDER_STATUSES[newStatus].label}`);
      loadOrders();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  // Get time elapsed since order was created
  const getElapsedTime = (createdAt) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  // Get urgency color based on time
  const getUrgencyColor = (createdAt, status) => {
    if (status === 'ready') return 'border-green-400';
    
    const created = new Date(createdAt);
    const now = new Date();
    const diffMins = Math.floor((now - created) / 60000);
    
    if (diffMins > 20) return 'border-red-500 animate-pulse';
    if (diffMins > 10) return 'border-amber-500';
    return 'border-gray-200';
  };

  // Group orders by status
  const pendingOrders = orders.filter(o => o.status === 'pending');
  const preparingOrders = orders.filter(o => o.status === 'preparing');
  const readyOrders = orders.filter(o => o.status === 'ready');

  return (
    <div className="h-[calc(100vh-140px)] flex flex-col" data-testid="kitchen-display">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-gray-900 text-white rounded-t-lg">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <ChefHat className="h-6 w-6 text-orange-400" />
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>Kitchen Display</h1>
          </div>
          
          <Select value={selectedRestaurant} onValueChange={setSelectedRestaurant}>
            <SelectTrigger className="w-[200px] bg-gray-800 border-gray-700 text-white">
              <SelectValue placeholder="All Restaurants" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Restaurants</SelectItem>
              {restaurants.map(r => (
                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-4">
          {/* Stats */}
          <div className="flex items-center gap-4 mr-4">
            <Badge className="bg-red-500 text-white px-3 py-1">
              <Bell className="h-3 w-3 mr-1" /> {pendingOrders.length} New
            </Badge>
            <Badge className="bg-amber-500 text-white px-3 py-1">
              <ChefHat className="h-3 w-3 mr-1" /> {preparingOrders.length} Cooking
            </Badge>
            <Badge className="bg-green-500 text-white px-3 py-1">
              <Check className="h-3 w-3 mr-1" /> {readyOrders.length} Ready
            </Badge>
          </div>
          
          {/* Controls */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="text-white hover:bg-gray-800"
          >
            {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`text-white hover:bg-gray-800 ${autoRefresh ? 'text-green-400' : ''}`}
          >
            <RefreshCw className={`h-4 w-4 ${autoRefresh ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={loadOrders}
            className="border-gray-600 text-white hover:bg-gray-800"
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Orders Grid */}
      <div className="flex-1 grid grid-cols-3 gap-4 p-4 bg-gray-100 overflow-hidden">
        {/* New Orders Column */}
        <div className="flex flex-col h-full">
          <div className="flex items-center gap-2 mb-3 px-2">
            <div className="h-3 w-3 rounded-full bg-red-500 animate-pulse" />
            <h2 className="font-bold text-gray-700">NEW ORDERS ({pendingOrders.length})</h2>
          </div>
          <div className="flex-1 overflow-y-auto space-y-3 pr-2">
            {pendingOrders.map(order => (
              <OrderCard 
                key={order.id} 
                order={order} 
                onStatusChange={updateOrderStatus}
                getElapsedTime={getElapsedTime}
                getUrgencyColor={getUrgencyColor}
                restaurants={restaurants}
              />
            ))}
            {pendingOrders.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No new orders</p>
              </div>
            )}
          </div>
        </div>

        {/* Preparing Column */}
        <div className="flex flex-col h-full">
          <div className="flex items-center gap-2 mb-3 px-2">
            <div className="h-3 w-3 rounded-full bg-amber-500" />
            <h2 className="font-bold text-gray-700">PREPARING ({preparingOrders.length})</h2>
          </div>
          <div className="flex-1 overflow-y-auto space-y-3 pr-2">
            {preparingOrders.map(order => (
              <OrderCard 
                key={order.id} 
                order={order} 
                onStatusChange={updateOrderStatus}
                getElapsedTime={getElapsedTime}
                getUrgencyColor={getUrgencyColor}
                restaurants={restaurants}
              />
            ))}
            {preparingOrders.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                <ChefHat className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Nothing cooking</p>
              </div>
            )}
          </div>
        </div>

        {/* Ready Column */}
        <div className="flex flex-col h-full">
          <div className="flex items-center gap-2 mb-3 px-2">
            <div className="h-3 w-3 rounded-full bg-green-500" />
            <h2 className="font-bold text-gray-700">READY ({readyOrders.length})</h2>
          </div>
          <div className="flex-1 overflow-y-auto space-y-3 pr-2">
            {readyOrders.map(order => (
              <OrderCard 
                key={order.id} 
                order={order} 
                onStatusChange={updateOrderStatus}
                getElapsedTime={getElapsedTime}
                getUrgencyColor={getUrgencyColor}
                restaurants={restaurants}
              />
            ))}
            {readyOrders.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                <Check className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No orders ready</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Order Card Component
function OrderCard({ order, onStatusChange, getElapsedTime, getUrgencyColor, restaurants }) {
  const statusInfo = ORDER_STATUSES[order.status];
  const StatusIcon = statusInfo?.icon || Bell;
  const restaurant = restaurants.find(r => r.id === order.restaurant_id);
  
  return (
    <Card 
      className={`border-2 ${getUrgencyColor(order.created_at, order.status)} transition-all hover:shadow-lg`}
      data-testid={`order-${order.id}`}
    >
      <CardHeader className="p-3 pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge className={`${statusInfo?.color} text-white`}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {statusInfo?.label}
            </Badge>
            {order.order_type === 'dine_in' && order.table_number && (
              <Badge variant="outline" className="font-bold">
                Table {order.table_number}
              </Badge>
            )}
            {order.order_type === 'room_service' && (
              <Badge variant="outline" className="bg-purple-50">
                Room {order.room_number || order.room_id?.slice(-4)}
              </Badge>
            )}
            {order.order_type === 'takeaway' && (
              <Badge variant="outline" className="bg-blue-50">
                Takeaway
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Timer className="h-3 w-3" />
            {getElapsedTime(order.created_at)}
          </div>
        </div>
        {restaurant && (
          <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
            <Store className="h-3 w-3" />
            {restaurant.name}
          </div>
        )}
      </CardHeader>
      <CardContent className="p-3 pt-0">
        {/* Order Items */}
        <div className="space-y-1 mb-3">
          {order.items?.map((item, idx) => (
            <div key={idx} className="flex justify-between items-center text-sm">
              <span className="font-medium">
                <span className="inline-flex items-center justify-center h-5 w-5 rounded bg-gray-200 text-xs font-bold mr-2">
                  {item.quantity}
                </span>
                {item.item_name}
              </span>
            </div>
          ))}
        </div>
        
        {/* Notes */}
        {order.notes && (
          <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-xs text-yellow-800 mb-3">
            <strong>Note:</strong> {order.notes}
          </div>
        )}
        
        {/* Customer */}
        {order.customer_name && (
          <p className="text-xs text-gray-500 mb-3">
            Customer: {order.customer_name}
          </p>
        )}
        
        {/* Action Button */}
        {statusInfo?.next && (
          <Button 
            className={`w-full ${
              order.status === 'pending' ? 'bg-amber-500 hover:bg-amber-600' :
              order.status === 'preparing' ? 'bg-green-500 hover:bg-green-600' :
              'bg-blue-500 hover:bg-blue-600'
            }`}
            onClick={() => onStatusChange(order.id, statusInfo.next)}
            data-testid={`mark-${statusInfo.next}-${order.id}`}
          >
            {order.status === 'pending' && (
              <>
                <Play className="h-4 w-4 mr-2" />
                Start Preparing
              </>
            )}
            {order.status === 'preparing' && (
              <>
                <Check className="h-4 w-4 mr-2" />
                Mark Ready
              </>
            )}
            {order.status === 'ready' && (
              <>
                <UtensilsCrossed className="h-4 w-4 mr-2" />
                Mark Served
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
